import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';

const Register = () => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const { registerUser, requestWhatsAppCode } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  // Watch password for confirmation validation
  const password = watch('password', '');
  
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      
      // Format the date for approved_terms_date
      const now = new Date().toISOString();
      
      // Register the user
      await registerUser({
        email: data.email,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone,
        gender: data.gender,
        approved_terms_date: now
      });
      
      // Request WhatsApp verification code
      try {
        await requestWhatsAppCode();
      } catch (whatsappErr) {
        console.error('WhatsApp verification error:', whatsappErr);
        // Continue even if WhatsApp verification fails
      }
      
      // Navigate to WhatsApp verification page
      navigate('/verify-whatsapp');
    } catch (err) {
      if (err.response?.data?.error_message) {
        setError(err.response.data.error_message);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('שגיאה בהרשמה. אנא נסה שנית.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-gray-900">הרשמה</h1>
          <p className="mt-2 text-sm text-gray-600">
            או{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
              התחברות למערכת
            </Link>
          </p>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
            <div>
              <label htmlFor="firstName" className="form-label">שם פרטי</label>
              <input
                id="firstName"
                type="text"
                {...register('firstName', { 
                  required: 'שם פרטי הוא שדה חובה'
                })}
                className="form-input"
                placeholder="הזן שם פרטי"
              />
              {errors.firstName && (
                <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="lastName" className="form-label">שם משפחה</label>
              <input
                id="lastName"
                type="text"
                {...register('lastName', { 
                  required: 'שם משפחה הוא שדה חובה'
                })}
                className="form-input"
                placeholder="הזן שם משפחה"
              />
              {errors.lastName && (
                <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>
              )}
            </div>
          </div>
          
          <div>
            <label htmlFor="email" className="form-label">אימייל</label>
            <input
              id="email"
              type="email"
              {...register('email', { 
                required: 'אימייל הוא שדה חובה',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'אנא הזן כתובת אימייל תקינה'
                }
              })}
              className="form-input"
              placeholder="הזן את כתובת האימייל שלך"
              dir="ltr"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
            )}
          </div>
          
          <div>
            <label htmlFor="phone" className="form-label">טלפון</label>
            <input
              id="phone"
              type="tel"
              {...register('phone', { 
                required: 'טלפון הוא שדה חובה',
                pattern: {
                  value: /^[0-9+\-\s()]*$/,
                  message: 'אנא הזן מספר טלפון תקין'
                }
              })}
              className="form-input"
              placeholder="הזן מספר טלפון"
              dir="ltr"
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>
            )}
          </div>
          
          <div>
            <label htmlFor="gender" className="form-label">מגדר</label>
            <select
              id="gender"
              {...register('gender', { 
                required: 'מגדר הוא שדה חובה'
              })}
              className="form-input"
              defaultValue=""
            >
              <option value="">בחר מגדר</option>
              <option value="male">זכר</option>
              <option value="female">נקבה</option>
            </select>
            {errors.gender && (
              <p className="text-red-500 text-xs mt-1">{errors.gender.message}</p>
            )}
          </div>
          
          <div>
            <label htmlFor="password" className="form-label">סיסמה</label>
            <input
              id="password"
              type="password"
              {...register('password', { 
                required: 'סיסמה היא שדה חובה',
                minLength: {
                  value: 6,
                  message: 'סיסמה חייבת להכיל לפחות 6 תווים'
                }
              })}
              className="form-input"
              placeholder="הזן סיסמה"
              dir="ltr"
            />
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
            )}
          </div>
          
          <div>
            <label htmlFor="confirmPassword" className="form-label">אימות סיסמה</label>
            <input
              id="confirmPassword"
              type="password"
              {...register('confirmPassword', { 
                required: 'אימות סיסמה הוא שדה חובה',
                validate: value => value === password || 'הסיסמאות אינן תואמות'
              })}
              className="form-input"
              placeholder="הזן שוב את הסיסמה"
              dir="ltr"
            />
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>
          
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="terms"
                type="checkbox"
                {...register('terms', { 
                  required: 'יש לאשר את תנאי השימוש'
                })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
            <div className="mr-3 text-sm">
              <label htmlFor="terms" className="font-medium text-gray-700">
                אני מאשר/ת את <a href="#" className="text-blue-600 hover:underline">תנאי השימוש</a> של האתר
              </label>
              {errors.terms && (
                <p className="text-red-500 text-xs mt-1">{errors.terms.message}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`btn-primary w-full ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? 'מבצע הרשמה...' : 'הרשמה'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
